import { Controller } from "@hotwired/stimulus"

// data-controller="carousel"
// Targets: slide, pager, modal, drawer, zoomImg
// Values: startIndex, loop
export default class extends Controller {
    static targets = ["slide", "pager", "modal", "drawer", "zoomImg"]
    static values = { startIndex: Number, loop: { type: Boolean, default: true } }

    connect() {
        this.index = this.hasStartIndexValue ? this.startIndexValue : 0
        this.viewport = this.element.querySelector("[data-carousel-viewport]")
        this.track = this.element.querySelector("[data-carousel-track]")
        this.update()

        // touch/mouse dragging
        this.pointerActive = false
        this.startX = 0
        this.currentX = 0
        this.track.style.touchAction = "pan-y"

        // Resize observer keeps slide width math correct
        this.ro = new ResizeObserver(() => this.snapTo(this.index))
        this.ro.observe(this.viewport)

        // Optional: double‑tap to like on mobile
        this.lastTap = 0
        this.element.addEventListener(
            "touchend",
            (e) => {
                const now = Date.now()
                if (now - this.lastTap < 300) {
                    const current = this.slideTargets[this.index]
                    current?.querySelector("#" + current.dataset.likeButtonId + " form button[type='submit']")?.click()
                    e.preventDefault()
                }
                this.lastTap = now
            },
            { passive: false }
        )
    }

    disconnect() { this.ro?.disconnect() }

    // Navigation
    next() { this._go(this.index + 1) }
    prev() { this._go(this.index - 1) }
    _go(i) {
        const last = this.slideTargets.length - 1
        if (i < 0) i = this.loopValue ? last : 0
        if (i > last) i = this.loopValue ? 0 : last
        this.snapTo(i)
    }

    goTo(event) { this.snapTo(parseInt(event.currentTarget.dataset.index, 10)) }

    onKeydown(event) {
        if (event.key === "ArrowRight") this.next()
        if (event.key === "ArrowLeft") this.prev()
        if (event.key === "Escape") this.closeOverlays()
    }

    // Drag/swipe handlers
    onPointerDown(event) {
        this.pointerActive = true
        this.startX = this._pointX(event)
        this.currentX = this.startX
        this.track.setPointerCapture?.(event.pointerId)
        this._boundMove ||= this.onPointerMove.bind(this)
        this._boundUp ||= this.onPointerUp.bind(this)
        this.track.addEventListener("pointermove", this._boundMove)
        this.track.addEventListener("pointerup", this._boundUp, { once: true })
    }

    onPointerMove(event) {
        if (!this.pointerActive) return
        this.currentX = this._pointX(event)
        const dx = this.currentX - this.startX
        this._translate(-this.index * this.viewport.clientWidth + dx)
    }

    onPointerUp() {
        this.track.removeEventListener("pointermove", this._boundMove)
        this.pointerActive = false
        const dx = this.currentX - this.startX
        const isSmall = window.innerWidth < 640
        const threshold = this.viewport.clientWidth * (isSmall ? 0.12 : 0.2)
        if (dx > threshold) this.prev()
        else if (dx < -threshold) this.next()
        else this.snapTo(this.index)
    }

    // Modal & drawer
    openModal(event) {
        const img = event.currentTarget?.querySelector("img")
        const src = img?.dataset.fullsrc || img?.src
        if (this.hasModalTarget && src) {
            this.modalTarget.classList.remove("hidden")
            const modalImg = this.modalTarget.querySelector("img")
            modalImg.src = src
            modalImg.alt = img?.alt || ""
            this.modalTarget.querySelector("button[data-close]").focus()
            document.body.style.overflow = "hidden"
        }
    }

    closeModal() {
        if (this.hasModalTarget) {
            this.modalTarget.classList.add("hidden")
            document.body.style.overflow = ""
            this.element.focus()
        }
    }

    toggleDrawer() {
        if (!this.hasDrawerTarget) return
        const opening = this.drawerTarget.classList.contains("translate-x-full")
        this.drawerTarget.classList.toggle("translate-x-full")
        document.body.style.overflow = opening ? "hidden" : ""

        if (opening) {
            // ensure comments for current slide are loaded when opening
            this._loadCommentsForCurrent()
            const focusable = this.drawerTarget.querySelector(
                "a, button, input, textarea, [tabindex]:not([tabindex='-1'])"
            )
            focusable?.focus()
        }
    }

    closeOverlays() {
        this.closeModal()
        if (this.hasDrawerTarget && !this.drawerTarget.classList.contains("translate-x-full")) this.toggleDrawer()
    }

    // Internals
    snapTo(i) {
        this.index = Math.max(0, Math.min(i, this.slideTargets.length - 1))
        const x = -this.index * this.viewport.clientWidth
        this.track.style.transition = "transform 250ms ease"
        this._translate(x)
        requestAnimationFrame(() => setTimeout(() => (this.track.style.transition = ""), 260))
        this._updatePager()
        this._loadCommentsForCurrent()
    }

    _translate(px) { this.track.style.transform = `translateX(${px}px)` }
    _pointX(e) { return e.touches ? e.touches[0].clientX : e.clientX }

    _updatePager() {
        this.pagerTargets.forEach((el, i) => {
            el.classList.toggle("opacity-100", i === this.index)
            el.classList.toggle("opacity-40", i !== this.index)
            el.setAttribute("aria-current", i === this.index ? "true" : "false")
        })
    }
}